import { useEffect } from 'react';
import { useMcpChatOnboarding } from '../../hooks/McpChatOnboardingContext';

const COMPOSER_INPUT_SELECTOR =
  '.aui-composer-input, textarea[aria-label="Message input"]';

const INTERACTIVE_SELECTOR = [
  '.aui-composer-send',
  'button[aria-label="Send message"]',
  '.aui-composer-cancel',
  '.mcp-chat-suggestions button'
].join(', ');

type McpChatOnboardingInteractionGuardProps = {
  enabled: boolean;
};

/**
 * Opens setup only when the user tries to type or send.
 * Mount autofocus is ignored so the modal stays closed on page entry.
 */
export function McpChatOnboardingInteractionGuard({
  enabled
}: McpChatOnboardingInteractionGuardProps) {
  const { needsOnboarding, requestOnboarding } = useMcpChatOnboarding();

  useEffect(() => {
    if (!enabled || !needsOnboarding) return;

    let fromUser = false;

    const inChat = (target: EventTarget | null): target is HTMLElement =>
      target instanceof HTMLElement && Boolean(target.closest('.mcp-chat-page'));

    const blockIfComposer = (target: EventTarget | null) => {
      if (!inChat(target)) return false;
      return Boolean(target.closest(COMPOSER_INPUT_SELECTOR));
    };

    const armIfChatIntent = (target: EventTarget | null) => {
      if (!inChat(target)) return;
      if (
        target.closest(COMPOSER_INPUT_SELECTOR) ||
        target.closest('.aui-composer-root') ||
        target.closest(INTERACTIVE_SELECTOR)
      ) {
        fromUser = true;
      }
    };

    const onPointerDown = (event: Event) => {
      armIfChatIntent(event.target);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        fromUser = true;
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (!blockIfComposer(event.target)) return;
      fromUser = true;
      event.preventDefault();
      requestOnboarding();
    };

    const onFocusIn = (event: FocusEvent) => {
      const user = fromUser;
      fromUser = false;
      if (!blockIfComposer(event.target)) return;
      const input = event.target as HTMLElement;
      input.blur();
      if (!user) return;
      requestOnboarding();
    };

    const onBeforeInput = (event: InputEvent) => {
      if (!blockIfComposer(event.target)) return;
      event.preventDefault();
      requestOnboarding();
    };

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!inChat(target)) return;
      const hit = target.closest(INTERACTIVE_SELECTOR);
      if (!hit) return;
      event.preventDefault();
      event.stopPropagation();
      requestOnboarding();
    };

    const alreadyFocused = document.activeElement;
    if (
      alreadyFocused instanceof HTMLElement &&
      alreadyFocused.closest('.mcp-chat-page') &&
      alreadyFocused.matches(COMPOSER_INPUT_SELECTOR)
    ) {
      alreadyFocused.blur();
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('focusin', onFocusIn, true);
    document.addEventListener('beforeinput', onBeforeInput, true);
    document.addEventListener('click', onClick, true);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('focusin', onFocusIn, true);
      document.removeEventListener('beforeinput', onBeforeInput, true);
      document.removeEventListener('click', onClick, true);
    };
  }, [enabled, needsOnboarding, requestOnboarding]);

  return null;
}
